import './libraryFolders.scss';

import React, { useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { LibraryFolder } from '@clipr/lib';
import { Link } from 'react-router-dom';
import Routes from '../../routes';
import { Button, TextInput } from '../input';
import { MenuItem } from '../input/menuItems';
import { MenuButton } from '../menu';
import { Window, WindowHeader } from '../overlays';
import { ChevronRightSVGIcon, FolderSVGIcon } from '../svg';
import type { TeamDashboardPageState } from '../../pages/teamDashboardPage/teamDashboardPageState';

type Props = { model: TeamDashboardPageState };

/** The drag payload: a JSON list of job ids. */
export const JOB_DRAG_TYPE = 'application/x-clipr-job-ids';
/** The drag payload: one tag, standing for every item in the library tagged with it. */
export const TAG_DRAG_TYPE = 'application/x-clipr-tag';

type FolderCardProps = Props & { item: LibraryFolder, teamId: string };

const FolderCard = observer(({ model, item, teamId }: FolderCardProps) => {
  const [isOver, setOver] = useState(false);
  // Pulses from the drop until the move settles, which can take a while for a tag with many items.
  const [isFiling, setFiling] = useState(false);
  // Dragging is a pointer shortcut; the card menu's "Move to folder…" is the keyboard path.
  const accepts = (event: React.DragEvent) => model.canMoveJobs
    && [JOB_DRAG_TYPE, TAG_DRAG_TYPE].some(type => event.dataTransfer.types.includes(type));
  const onDrop = (event: React.DragEvent) => {
    if (!accepts(event)) return;
    event.preventDefault();
    setOver(false);
    const tag = event.dataTransfer.getData(TAG_DRAG_TYPE);
    setFiling(true);
    void (tag
      ? model.moveTaggedJobs(tag, item.id)
      : model.moveJobs(JSON.parse(event.dataTransfer.getData(JOB_DRAG_TYPE)), item.id)
    ).finally(() => setFiling(false));
  };

  return (
    <div
      className={classNames('library-folder-card', { 'drag-over': isOver, filing: isFiling })}
      aria-busy={isFiling}
      onDragOver={(event) => { if (accepts(event)) { event.preventDefault(); setOver(true); } }}
      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOver(false); }}
      onDrop={onDrop}>
      <Link className="library-folder-link" to={`${Routes.teamDashboard(teamId)}?folder=${encodeURIComponent(item.id)}`}>
        <FolderSVGIcon />
        <span className="library-folder-name" title={item.name}>{item.name}</span>
      </Link>
      {model.canDeleteFolders &&
        <MenuButton className="btn icon-btn" aria-label={`Folder actions for ${item.name}`}>
          <MenuItem
            model={{ value: 'delete', label: 'Delete' }}
            onClick={() => model.openDeleteFolder(item)} />
        </MenuButton>}
    </div>
  );
});

/** Folder cards at the library root, or the breadcrumb once a folder is open. */
export const LibraryFolders = observer(({ model }: Props) => {
  const { teamId, folder, folders } = model;
  if (!teamId) return null;

  if (folder) {
    return (
      <nav className="library-breadcrumb" aria-label="Breadcrumb">
        <Link to={Routes.teamDashboard(teamId)}>Library</Link>
        <ChevronRightSVGIcon />
        <span aria-current="page">{folder.name}</span>
      </nav>
    );
  }

  // Kept under a search or tag filter too: filtering by a tag, then dropping on a folder, is how cases get filed.
  if (!folders.length) return null;

  return (
    <div className="library-folders">
      {folders.map(item => <FolderCard key={item.id} model={model} item={item} teamId={teamId} />)}
    </div>
  );
});

export const LibraryFolderWindow = observer(({ model }: Props) => {
  const dialog = model.folderDialog;
  const deleting: LibraryFolder | null = dialog?.type === 'delete' ? dialog.folder : null;
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void model.submitFolderDialog();
  };

  return (
    <Window model={model.folderWindow} visible={!!dialog} isLoading={model.isSavingFolder}>
      <WindowHeader model={model.folderWindow} title={deleting ? 'Delete folder' : 'New folder'} />
      <hr />
      <form onSubmit={submit}>
        <div className="window-content">
          {deleting ?
            <>
              <p className="p-bold">Delete "{deleting.name}"?</p>
              <p>Its items move back to the library.</p>
            </> :
            <TextInput
              label="Folder name"
              placeholder="Enter folder name"
              persistentLabel
              maxLength={100}
              autoFocus
              value={model.folderName}
              onChange={(event) => model.setFolderName(event.target.value)} />}
        </div>
        <hr />
        <footer className="window-footer">
          <div className="btn-group right">
            <Button label="Cancel" className="primary btn-outline medium"
              onClick={() => model.closeFolderDialog()} disabled={model.isSavingFolder} />
            <Button type="submit" className="primary medium" label={deleting ? 'Delete' : 'Create'}
              disabled={model.isSavingFolder || (!deleting && !model.folderName.trim())} />
          </div>
        </footer>
      </form>
    </Window>
  );
});

/** Picker for "Move to folder…": the library root, then each folder. */
export const MoveToFolderWindow = observer(({ model }: Props) => {
  const ids = model.moveJobIds;
  const options: { id: string | null; name: string }[] = model.folders.filter(f => f.id !== model.folder?.id);
  if (model.folder) options.unshift({ id: null, name: 'Library (no folder)' });

  return (
    <Window model={model.moveWindow} visible={!!ids} isLoading={model.isMoving}>
      <WindowHeader model={model.moveWindow} title={`Move ${ids?.length ?? 0} ${ids?.length === 1 ? 'item' : 'items'} to`} />
      <hr />
      <div className="window-content move-to-folder-options">
        {options.map(option => (
          <Button key={option.id ?? 'root'} label={option.name} className="primary btn-outline medium"
            onClick={() => void model.moveJobs(ids ?? [], option.id)} />
        ))}
      </div>
      <hr />
      <footer className="window-footer">
        <div className="btn-group right">
          <Button label="Cancel" className="primary btn-outline medium" onClick={() => model.closeMoveToFolder()} />
        </div>
      </footer>
    </Window>
  );
});
